import {
    Column,
    Container,
    cssVar,
    Divider,
    EditIcon,
    GenericModal,
    Grid,
    getFormattedDate,
    Header,
    IconButton,
    Label,
    PrimaryButton,
    RenewalSmallIcon,
    Row,
    TextArea,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

const Button = styled.div`
    opacity: 0.75;
    :hover {
        opacity: 1;
    }
`;

const StyledTextArea = styled(TextArea)`
    border: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
    //padding: .25rem;
    //height: 150px;
`;

const LeftHalfBorderColumn = styled(Column)`
    padding: 1rem 0 1rem 1rem;
    border-left: 1px solid ${cssVar.colorStrokeDark};
    border-bottom: 1px solid ${cssVar.colorStrokeDark};
    border-top: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
`;

const RightHalfColumn = styled(Column)`
    padding: 1rem 1rem 1rem 0;
    border-right: 1px solid ${cssVar.colorStrokeDark};
    border-bottom: 1px solid ${cssVar.colorStrokeDark};
    border-top: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
`;

const MiddleColumn = styled(Column)`
    padding: 1rem 0;
    border-bottom: 1px solid ${cssVar.colorStrokeDark};
    border-top: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
`;

const ProfileNotes = (props) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [remark, setRemark] = useState('');
    const [editingNote, setEditingNote] = useState(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.doGetNotes();
    }, []);

    const addNoteBtnClick = () => {
        setShowAddModal(true);
    };

    const editNoteBtnClick = (note) => {
        setEditingNote(note);
        setRemark(note.remark);
        setShowEditModal(true);
    };

    const closeAddModal = () => {
        setRemark('');
        setShowAddModal(false);
    };

    const closeEditModal = () => {
        setRemark('');
        setEditingNote(null);
        setShowEditModal(false);
    };

    const setRemarkValue = (value) => {
        setRemark(value);
    };

    const saveNoteHandler = () => {
        props.doUpsertNote({
            remark: remark,
            createdBy: props.context.session.user,
            createdByName: props.context.session.fullName,
            createdOn: new Date().toISOString(),
        });
        closeAddModal();
    };

    const updateNoteHandler = () => {
        props.doUpsertNote({
            noteGuid: editingNote.guid,
            remark: remark,
        });
        closeEditModal();
    };

    const { context, notes, doGetNotes } = props;

    return (
        <Container>
            <Divider top={sizes.xl} />
            <Header size={sizes.lg} textAlign={alignments.left} text={<FormattedMessage id='Profile_Notes.notes' />} />
            <Divider top={sizes.lg} />
            <Header
                size={sizes.md}
                bottom={sizes.xs}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_Notes.action' />}
            />
            <Container top={sizes.xs} bottom={sizes.sm} inline>
                <IconButton
                    text={<FormattedMessage id='Profile_Notes.refresh' />}
                    size={sizes.sm}
                    right={sizes.sm}
                    icon={<RenewalSmallIcon color={cssVar.colorLightMode} />}
                    clickEvent={doGetNotes}
                />
                <PrimaryButton
                    text={<FormattedMessage id='Profile_Notes.addNew' />}
                    size={sizes.sm}
                    clickEvent={addNoteBtnClick}
                />
            </Container>
            {notes?.length > 0 && (
                <Grid>
                    {notes?.map((note) => (
                        <Row key={note.guid}>
                            <LeftHalfBorderColumn nogutter xs={8}>
                                <Label textAlign={alignments.left} text={note.remark} size={sizes.lg} bold />
                            </LeftHalfBorderColumn>
                            <MiddleColumn nogutter xs={3}>
                                <Container
                                    fullSize
                                    verticalAlignment={alignments.top}
                                    horizontalAlignment={alignments.left}
                                >
                                    <Label
                                        textAlign={alignments.left}
                                        text={intl.formatMessage(
                                            { id: 'Profile_Notes.by' },
                                            {
                                                name: note.modifiedByName,
                                                date: getFormattedDate(note.modifiedOn, context.session.culture),
                                            },
                                        )}
                                        left={sizes.xs}
                                    />
                                </Container>
                            </MiddleColumn>
                            <RightHalfColumn nogutter xs={0.5}>
                                <Container
                                    fullSize
                                    verticalAlignment={alignments.center}
                                    horizontalAlignment={alignments.right}
                                >
                                    <Button onClick={() => editNoteBtnClick(note)}>
                                        <EditIcon />
                                    </Button>
                                </Container>
                            </RightHalfColumn>
                        </Row>
                    ))}
                </Grid>
            )}
            {showAddModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={closeAddModal}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Notes.addNewNote' />}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <StyledTextArea
                                    text={remark}
                                    changeEvent={setRemarkValue}
                                    placeholder={intl.formatMessage({ id: 'Profile_Notes.enterYourNoteHere' })}
                                />
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Notes.cancel' />,
                            disabled: false,
                            clickEvent: closeAddModal,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Notes.add' />,
                            disabled: false,
                            clickEvent: saveNoteHandler,
                        },
                    ]}
                />
            )}
            {showEditModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={closeEditModal}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Notes.editNote' />}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <StyledTextArea text={remark} changeEvent={setRemarkValue} />
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Notes.cancel' />,
                            disabled: false,
                            clickEvent: closeEditModal,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Notes.update' />,
                            disabled: false,
                            clickEvent: updateNoteHandler,
                        },
                    ]}
                />
            )}
        </Container>
    );
};

export default ProfileNotes;
